import React from 'react';
import { NavLink } from './NavLink';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeProvider';
import { Dumbbell, Users, Calendar, Clock, Settings, LogOut, PanelLeft, LayoutDashboard, Cog, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import ThemeSwitcher from '@/components/common/ThemeSwitcher';
import OrganizationSwitcher from '@/components/common/OrganizationSwitcher';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Sidebar as SidebarPrimitive, SidebarContent, SidebarHeader, SidebarFooter, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
interface SidebarProps {
  // Props for mobile/tablet overlay mode
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}
const Sidebar: React.FC<SidebarProps> = ({
  open: mobileOpen,
  onOpenChange: setMobileOpen
}) => {
  const {
    profile,
    currentOrganization,
    currentUserRole,
    signOut
  } = useAuth();
  const {
    t
  } = useTranslation();
  const {
    state,
    toggleSidebar
  } = useSidebar();
  const {
    theme,
    systemTheme
  } = useTheme();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Get the appropriate logo based on theme, fallback to either logo if only one exists
  const getCurrentLogo = () => {
    if (!currentOrganization) return null;
    const {
      logo_light_url,
      logo_dark_url
    } = currentOrganization;

    // Use systemTheme when theme is 'system', otherwise use theme directly
    const currentTheme = theme === 'system' ? systemTheme : theme;

    // Debug logging
    console.log('Theme Debug:', {
      theme,
      systemTheme,
      currentTheme,
      logo_light_url,
      logo_dark_url,
      organizationName: currentOrganization.name
    });

    // If theme is dark and dark logo exists, use it
    if (currentTheme === 'dark' && logo_dark_url) {
      console.log('Using dark logo:', logo_dark_url);
      return logo_dark_url;
    }

    // If theme is light and light logo exists, use it
    if (currentTheme === 'light' && logo_light_url) {
      console.log('Using light logo:', logo_light_url);
      return logo_light_url;
    }

    // Fallback: use whichever logo exists
    const fallbackLogo = logo_light_url || logo_dark_url;
    console.log('Using fallback logo:', fallbackLogo);
    return fallbackLogo;
  };

  // Get signed URL for current logo
  const currentLogoUrl = getCurrentLogo();
  const {
    signedUrl: logoUrl
  } = useSignedUrl(currentLogoUrl || null, 'gym-logos');
  console.log('QQQQSidebar: Current user role:', profile, currentUserRole);
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'manager':
        return 'default';
      default:
        return 'secondary';
    }
  };
  const getRoleTranslation = (role: string) => {
    switch (role) {
      case 'admin':
        return t('users.admin');
      case 'manager':
        return t('users.manager');
      default:
        return t('users.user');
    }
  };
  const menuItems = [];

  // Add dashboard first for managers and admins
  if (currentUserRole === 'admin' || currentUserRole === 'manager') {
    menuItems.push({
      title: t('navigation.dashboard'),
      url: '/dashboard',
      icon: LayoutDashboard
    });
  }

  // Add other menu items
  menuItems.push({
    title: t('navigation.calendar'),
    url: '/',
    icon: Calendar
  }, {
    title: t('navigation.workoutTypes'),
    url: '/workout-types',
    icon: Dumbbell
  }, {
    title: t('navigation.clients'),
    url: '/clients',
    icon: Users
  }, {
    title: t('navigation.schedule'),
    url: '/schedule',
    icon: Clock
  });
  
  // Add notes for managers and admins
  if (currentUserRole === 'admin' || currentUserRole === 'manager') {
    menuItems.push({
      title: t('navigation.notes'),
      url: '/notes',
      icon: FileText
    });
  }
  
  if (currentUserRole === 'admin') {
    menuItems.push({
      title: t('navigation.userManagement'),
      url: '/users',
      icon: Settings
    }, {
      title: t('navigation.gymSettings'),
      url: '/gym-settings',
      icon: Cog
    });
  }
  const handleNavigation = () => {
    if (setMobileOpen) {
      setMobileOpen(false);
    }
  };

  // Determine sidebar width based on device
  const sidebarWidth = isTablet ? "w-80" : "w-full";

  // Mobile and Tablet view - Sheet overlay
  if (isMobile || isTablet) {
    return <Sheet open={mobileOpen || false} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className={`${sidebarWidth} p-0 flex flex-col pwa-safe-top`}>
          <SheetHeader className="p-6 border-b pwa-safe-top">
            <SheetTitle className="text-left">
              {currentLogoUrl && logoUrl ? <div className="w-[120px] rounded-lg overflow-hidden mx-auto">
                  <img src={logoUrl} alt={currentOrganization.name} className="w-full h-full object-contain" />
                </div> : <div className="flex flex-col space-y-1">
                  <h1 className="text-xl font-bold text-primary">StudioSync</h1>
                  {currentOrganization && <p className="text-sm text-muted-foreground font-normal">{currentOrganization.name}</p>}
                </div>}
            </SheetTitle>
            <OrganizationSwitcher />
          </SheetHeader>
          
          <div className="flex-1 flex flex-col">
            <nav className="flex-1 p-6">
              <div className="space-y-2">
                {menuItems.map(item => <NavLink key={item.url} to={item.url} icon={<item.icon className="h-5 w-5" />} className="flex items-center space-x-3 px-3 py-3 rounded-lg text-foreground hover:bg-accent hover:text-accent-foreground transition-colors w-full" onClick={handleNavigation}>
                    <span className="font-medium">{item.title}</span>
                  </NavLink>)}
              </div>
            </nav>
            
            <div className="border-t p-6 space-y-4">
              <div className="space-y-3">
                <LanguageSwitcher />
                <ThemeSwitcher />
              </div>
              
              {profile && currentUserRole && <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {profile.full_name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{profile.full_name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={getRoleColor(currentUserRole)} className="text-xs">
                          {getRoleTranslation(currentUserRole)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <Button variant="outline" onClick={signOut} className="w-full flex items-center gap-2">
                    <LogOut className="h-4 w-4" />
                    <span>{t('auth.signOut')}</span>
                  </Button>
                </div>}
            </div>
          </div>
        </SheetContent>
      </Sheet>;
  }

  // Desktop view - SidebarPrimitive
  return <>
      <SidebarPrimitive>
        <SidebarHeader className="border-b border-border">
          <div className="flex items-center justify-between p-4">
            <div className="flex-1">
              {currentLogoUrl && logoUrl ? <div className="w-[200px] rounded-lg overflow-hidden">
                  <img src={logoUrl} alt={currentOrganization.name} className="w-full h-full object-contain" />
                </div> : <>
                  <h1 className="text-xl font-bold text-primary">StudioSync</h1>
                  {currentOrganization && <p className="text-sm text-muted-foreground">
                      {currentOrganization.name}
                    </p>}
                </>}
            </div>
            <SidebarTrigger className="group-data-[collapsible=icon]:hidden ml-2" />
          </div>
            <OrganizationSwitcher />
        </SidebarHeader>

        <SidebarContent className="p-4">
          <SidebarMenu>
            {menuItems.map(item => <SidebarMenuItem key={item.url}>
                <SidebarMenuButton asChild tooltip={item.title}>
                  <NavLink to={item.url} icon={<item.icon className="h-5 w-5" />}>
                    <span>{item.title}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>)}
          </SidebarMenu>
        </SidebarContent>

        <SidebarFooter className="border-t border-border p-4 space-y-3">
          <div className="flex flex-col gap-2 group-data-[collapsible=icon]:hidden">
            <LanguageSwitcher />
            <ThemeSwitcher />
          </div>

          {profile && currentUserRole && <div className="space-y-3">
              <div className="flex items-center space-x-3 group-data-[collapsible=icon]:justify-center">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {profile.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                  <div className="font-medium truncate">
                    {profile.full_name}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getRoleColor(currentUserRole)} className="text-xs">
                      {getRoleTranslation(currentUserRole)}
                    </Badge>
                  </div>
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={signOut} className="w-full flex items-center gap-2 group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:p-0">
                    <LogOut className="h-4 w-4" />
                    <span className="group-data-[collapsible=icon]:hidden">
                      {t('auth.signOut')}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" align="center" hidden={state !== 'collapsed'}>
                  {t('auth.signOut')}
                </TooltipContent>
              </Tooltip>
            </div>}
        </SidebarFooter>
      </SidebarPrimitive>

      {/* Toggle button for collapsed state */}
      {state === 'collapsed' && <div className="fixed top-4 left-4 z-50 hidden md:block">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={toggleSidebar} className="h-8 w-8">
                <PanelLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {t('navigation.openSidebar')}
            </TooltipContent>
          </Tooltip>
        </div>}
    </>;
};
export default Sidebar;